
const libPictView = require('pict-view');

const libGraphRenderer = require('../approval-workflow/ApprovalWorkflow-GraphRenderer.js');
const libModel = require('../approval-workflow/ApprovalWorkflow-Model.js');
// Loading this sets window.ApprovalWorkflowHelp, which the inline (i) help icons in the templates call.
require('../approval-workflow/ApprovalWorkflow-Help.js');

/**
 * ApprovalWorkflowEditor — the #/ApprovalWorkflow/:hash visual editor.
 *
 * The diagram is rendered by GRAPHVIZ (via ApprovalWorkflow-GraphRenderer): graphviz lays out the states,
 * routes the spline edges with proper attachment + arrowheads, places non-overlapping labels, and minimises
 * crossings — the things we previously hand-reconstructed in pict-section-flow. This view owns the chrome,
 * the side panels, and the editing flow; clicking a node/edge in the SVG opens the matching panel. All edits
 * mutate the working blob through ApprovalWorkflowProvider (the single source of truth); Save posts it back.
 */
class ApprovalWorkflowEditor extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;

		// The graphviz renderer + the container it is bound to (re-bound if the wrap re-renders a fresh one)
		// + the workflow object last drawn (so a full-view re-render doesn't redraw needlessly).
		this._renderer = null;
		this._rendererContainer = null;
		this._renderedWorkflow = null;
		// Edit state.
		this._editMode = false;
		// The state currently highlighted in the steps rail / graph.
		this._activeStateStatus = null;
	}

	get provider()
	{
		return this.pict.providers.ApprovalWorkflow;
	}

	get editorState()
	{
		return this.provider ? this.provider.state.Editor : { Loading: false, Error: '', Workflow: null, Hash: '', IsNew: false, Editable: false, Dirty: false };
	}

	get modal()
	{
		return this.pict.views['Pict-Section-Modal'];
	}

	onBeforeRender(pRenderable)
	{
		const tmpEditor = this.editorState;
		const tmpAppState = this.pict.AppData.ApprovalWorkflowEditor || {};
		tmpAppState.EditorLoadingSlot = tmpEditor.Loading ? [ {} ] : [];
		tmpAppState.EditorErrorSlot = tmpEditor.Error ? [ { Message: tmpEditor.Error } ] : [];
		tmpAppState.EditorTitle = tmpEditor.IsNew ? 'New Approval Workflow' : this._esc(tmpEditor.Hash || 'Approval Workflow');
		// Leaving a workflow that is no longer loaded resets edit mode.
		if (!tmpEditor.Editable) { this._editMode = false; }
		return super.onBeforeRender(pRenderable);
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		const tmpEditor = this.editorState;
		if (tmpEditor.Workflow)
		{
			this._ensureRenderer();
			if (this._renderedWorkflow !== tmpEditor.Workflow)
			{
				this._renderedWorkflow = tmpEditor.Workflow;
				this._activeStateStatus = null; // a freshly loaded workflow has nothing selected yet
				this._renderGraph(false);
			}
		}
		this._renderActions();
		this._renderRail();
		this._renderLevelBanner();
		this._ensureUnloadGuard();
		this.pict.CSSMap.injectCSS();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	/** A different workflow is loading: nothing about the previous one's editing session carries over. */
	onWorkflowLoadStarted()
	{
		this._editMode = false;
		this._editRefStatus = null;
		this._editStateStatus = null;
		this._closeDetail();
	}

	/** Where the workflow is set became known. */
	onLevelInfoLoaded()
	{
		this._renderLevelBanner();
		this._renderActions();
	}

	/** Say which level is open, and what editing it does and doesn't reach. */
	_renderLevelBanner()
	{
		if (!this.pict.ContentAssignment.getElement('#ApprovalWorkflow-LevelBanner'))
		{
			return;
		}
		const tmpEditor = this.editorState;
		const tmpInfo = tmpEditor.LevelInfo;
		let tmpMessage = '';
		let tmpLinks = [];
		let tmpRevertLabel = '';
		if (tmpEditor.IDProject)
		{
			const tmpName = (tmpInfo && tmpInfo.ProjectName) || `project ${tmpEditor.IDProject}`;
			tmpMessage = (tmpInfo && !tmpInfo.OnThisProject)
				? `Viewing this workflow as ${this._esc(tmpName)} inherits it. Saving creates a copy for this project.`
				: `Editing the copy on ${this._esc(tmpName)}. Changes to the customer's workflow don't reach it.`;
			if (tmpInfo && tmpInfo.OnThisProject)
			{
				tmpRevertLabel = (tmpInfo.OnCustomer || tmpInfo.OnGlobal) ? 'Remove copy and use the customer\'s' : 'Delete this copy';
			}
		}
		else if (tmpInfo)
		{
			if (tmpInfo.OnGlobal && !tmpInfo.OnCustomer)
			{
				tmpMessage = 'This is the global default. Saving creates a copy for your organization.';
			}
			else if (tmpInfo.OnGlobal)
			{
				tmpMessage = 'Your organization has customized this from the global default.';
				tmpRevertLabel = 'Revert to the global default';
			}
			if (tmpInfo.Projects.length)
			{
				const tmpCount = tmpInfo.Projects.length;
				tmpMessage += `${tmpMessage ? ' ' : ''}${tmpCount} project${(tmpCount === 1) ? ' has its' : 's have their'} own cop${(tmpCount === 1) ? 'y' : 'ies'}, which changes here don't reach:`;
				tmpLinks = tmpInfo.Projects.map((pProject) => ({ Href: `#/ApprovalWorkflow/${encodeURIComponent(tmpEditor.Hash)}/Project/${pProject.IDProject}`, Label: this._esc(pProject.ProjectName) }));
			}
		}
		const tmpRecord = { Slot: tmpMessage ? [ { Message: tmpMessage, Links: tmpLinks, RevertSlot: (tmpRevertLabel && tmpEditor.Editable) ? [ { Label: tmpRevertLabel } ] : [] } ] : [] };
		this.pict.ContentAssignment.assignContent('#ApprovalWorkflow-LevelBanner', this.pict.parseTemplateByHash('ApprovalWorkflow-LevelBanner-Template', tmpRecord));
	}

	/** Stop overriding what's inherited. */
	async revertToInherited()
	{
		const tmpEditor = this.editorState;
		const tmpOk = await this.modal.confirm(tmpEditor.IDProject ? 'Remove this project\'s copy of the workflow? Its changes will be lost, and documents already in a state only that copy has can\'t be acted on afterwards.' : 'Remove your organization\'s changes and use the global default? Your changes will be lost, and documents already in a state only your copy has can\'t be acted on afterwards.', { title: 'Remove copy', confirmLabel: 'Remove', cancelLabel: 'Cancel', dangerous: true });
		if (!tmpOk)
		{
			return;
		}
		try
		{
			await this.provider.revertToInheritedAsync();
			this.modal.toast('Copy removed.', { type: 'success' });
			window.location.hash = '#/ApprovalWorkflows';
		}
		catch (pError)
		{
			this.modal.toast(`Couldn't remove it: ${pError && pError.message ? pError.message : pError}`, { type: 'error', duration: 8000 });
		}
	}

	/** Delete the workflow, once nothing uses it. */
	async deleteWorkflow()
	{
		try
		{
			const tmpBlockers = await this.provider.workflowDeleteBlockersAsync(this.editorState.Hash);
			if (tmpBlockers.length)
			{
				await this.modal.show(
					{
						title: 'This workflow can\'t be deleted yet',
						width: 'min(720px, 92vw)',
						content: `<div class="apwe-preview">${tmpBlockers.map((pLine) => `<div class="apwe-preview-line">${this._esc(pLine)}</div>`).join('')}</div>`,
						buttons: [ { Hash: 'cancel', Label: 'Close' } ],
					});
				return;
			}
			const tmpOk = await this.modal.confirm(`Delete the workflow "${this.editorState.Hash}"? This can't be undone.`, { title: 'Delete workflow', confirmLabel: 'Delete', cancelLabel: 'Cancel', dangerous: true });
			if (!tmpOk)
			{
				return;
			}
			const tmpResult = await this.provider.deleteWorkflowAsync();
			if (tmpResult.Deleted)
			{
				this.modal.toast('Workflow deleted.', { type: 'success' });
				window.location.hash = '#/ApprovalWorkflows';
			}
			else
			{
				this.modal.toast(tmpResult.Blockers.join(' '), { type: 'error', duration: 8000 });
			}
		}
		catch (pError)
		{
			this.modal.toast(`Couldn't delete it: ${pError && pError.message ? pError.message : pError}`, { type: 'error', duration: 8000 });
		}
	}

	/** Warn before a tab close or reload throws away unsaved edits. In-app navigation is guarded in the router's before-hook. */
	_ensureUnloadGuard()
	{
		if (this._unloadGuardBound || (typeof window === 'undefined'))
		{
			return;
		}
		this._unloadGuardBound = true;
		window.addEventListener('beforeunload', (pEvent) =>
		{
			if (this.provider && this.provider.hasUnsavedEdits())
			{
				pEvent.preventDefault();
				pEvent.returnValue = '';
			}
		});
	}

	// --- Graph (graphviz) ----------------------------------------------------------------------------

	/** Create the renderer once (re-created + re-bound if the wrap re-rendered a fresh container). */
	_ensureRenderer()
	{
		const tmpContainer = document.getElementById('ApprovalWorkflow-Flow-Container');
		if (!tmpContainer || (this._renderer && this._rendererContainer === tmpContainer)) { return; }
		if (this._renderer) { this._renderer.destroy(); }
		this._renderer = new libGraphRenderer(tmpContainer,
			{
				onNodeClick: (pStatus) => this._onNodeClick(pStatus),
				onEdgeClick: (pInfo) => this._onEdgeClick(pInfo),
				// Once graphviz has laid the graph out, re-list the rail in that exact top-to-bottom order.
				onRendered: () => this._renderRail(),
			});
		this._rendererContainer = tmpContainer;
		this._renderedWorkflow = null;
	}

	/** Draw the current working blob. pKeepView preserves pan/zoom (for incremental edits). */
	_renderGraph(pKeepView)
	{
		if (!this._renderer || !this.provider.workflow) { return; }
		this._renderer.render(this.provider.workflow, { keepView: !!pKeepView });
	}

	/** Re-draw after an edit, keeping the viewport. Called by every editing action. */
	_refreshGraph()
	{
		this._renderedWorkflow = this.provider.workflow;
		this._renderGraph(true);
		this._renderRail();
	}

	// On-canvas zoom controls (the floating +/−/fit cluster bottom-right of the graph).
	graphZoomIn() { if (this._renderer) { this._renderer.zoomBy(0.8); } }
	graphZoomOut() { if (this._renderer) { this._renderer.zoomBy(1 / 0.8); } }
	graphFit() { if (this._renderer) { this._renderer.fit(); } }

	/** Toggle the on-canvas colour legend. */
	toggleLegend()
	{
		const tmpEl = document.getElementById('ApprovalWorkflow-Legend');
		if (tmpEl) { tmpEl.classList.toggle('is-collapsed'); }
	}

	async _onNodeClick(pStatus)
	{
		const tmpCommit = await this._commitOpenPanel();
		if (!tmpCommit.OK)
		{
			return;
		}
		const tmpStatus = (tmpCommit.RenamedFrom === pStatus) ? tmpCommit.RenamedTo : pStatus;
		this._setActiveState(tmpStatus);
		if (this._editMode)
		{
			this._showStateEditor(tmpStatus);
		}
		else
		{
			this._showStateDetail(tmpStatus);
		}
	}

	// --- Steps rail (the ordered state list beside the diagram) --------------------------------------

	/** Mark a state active in the rail + outline it in the graph. */
	_setActiveState(pStatus)
	{
		this._activeStateStatus = pStatus;
		if (this._renderer) { this._renderer.highlightStatus(pStatus); }
		this._renderRail();
	}

	/** Clicking a step selects that state (opens its panel + highlights it), same as clicking its node. */
	selectStateFromRail(pStatus)
	{
		this._onNodeClick(pStatus);
	}

	/** Collapse / expand the steps rail (its state lives as a class on the static wrap). */
	toggleRail()
	{
		const tmpWrap = document.getElementById('ApprovalWorkflow-RailWrap');
		if (tmpWrap) { tmpWrap.classList.toggle('is-collapsed'); }
	}

	/** Render the ordered list of states into the rail, in the diagram's top-to-bottom order. */
	_renderRail()
	{
		if (!this.pict.ContentAssignment.getElement('#ApprovalWorkflow-Rail')) { return; }
		const tmpEditing = !!(this._editMode && this.editorState.Editable);
		const tmpStates = (this.provider.workflow && this.provider.workflow.States) || [];
		const tmpSteps = this._railOrder(tmpStates).map((pIndex) =>
		{
			const tmpState = tmpStates[pIndex];
			return {
				Index: pIndex,
				Status: this._esc(tmpState.Status),
				StateName: this._esc(tmpState.StateName || tmpState.Status),
				Color: libModel.stateColor(tmpState.Status),
				Tint: libModel.stateTint(tmpState.Status),
				OutCount: (tmpState.Transitions || []).length,
				DocumentText: this._documentCountText(tmpState.Status),
				ActiveClass: (tmpState.Status === this._activeStateStatus) ? 'is-active' : '',
			};
		});
		this.pict.ContentAssignment.assignContent('#ApprovalWorkflow-Rail',
			this.pict.parseTemplateByHash('ApprovalWorkflow-Rail-Template',
				{ Steps: tmpSteps, CountText: `${tmpStates.length}`, EmptySlot: tmpSteps.length ? [] : [ {} ], AddStateSlot: tmpEditing ? [ {} ] : [] }));
	}

	/** The rail's document count for a state; empty until counts are loaded or when there are none. */
	_documentCountText(pStatus)
	{
		const tmpCount = this.provider.documentCountFor(pStatus);
		return tmpCount ? ` · ${tmpCount.toLocaleString()} doc${(tmpCount === 1) ? '' : 's'}` : '';
	}

	/**
	 * The state indices in the order the rail should list them: the diagram's ACTUAL laid-out order once
	 * graphviz has rendered (so the rail matches the graph exactly, cycles and all), falling back to the
	 * computed flow order before the async layout is ready. States not yet in the layout (e.g. just added)
	 * are appended in array order.
	 * @param {Array} pStates
	 * @return {Array<Number>}
	 */
	_railOrder(pStates)
	{
		const tmpStatusOrder = this._renderer ? this._renderer.getStatusOrder() : null;
		if (!tmpStatusOrder || !tmpStatusOrder.length)
		{
			return libModel.flowOrder(this.provider.workflow);
		}
		const tmpIndexByStatus = {};
		pStates.forEach((pState, pIndex) => { tmpIndexByStatus[pState.Status] = pIndex; });
		const tmpOrdered = [];
		const tmpSeen = {};
		tmpStatusOrder.forEach((pStatus) =>
		{
			if (tmpIndexByStatus.hasOwnProperty(pStatus) && !tmpSeen[pStatus]) { tmpSeen[pStatus] = true; tmpOrdered.push(tmpIndexByStatus[pStatus]); }
		});
		pStates.forEach((pState, pIndex) => { if (!tmpSeen[pState.Status]) { tmpOrdered.push(pIndex); } });
		return tmpOrdered;
	}

	async _onEdgeClick(pInfo)
	{
		const tmpCommit = await this._commitOpenPanel();
		if (!tmpCommit.OK)
		{
			return;
		}
		if (this._editMode && !pInfo.IsBranch)
		{
			this._showTransitionEditor(pInfo.SourceStatus, pInfo.TransitionIndex);
			return;
		}
		const tmpDetail = this._transitionDetailRecord(pInfo);
		if (!tmpDetail) { return; }
		if (pInfo.IsBranch) { this._showBranchReadonly(tmpDetail); }
		else { this._showTransitionDetail(tmpDetail); }
	}

	// --- Edit-mode chrome ----------------------------------------------------------------------------

	async toggleEditMode()
	{
		if (!this.editorState.Editable) { return; }
		const tmpCommit = await this._commitOpenPanel();
		if (!tmpCommit.OK)
		{
			return;
		}
		this._editMode = !this._editMode;
		this._closeDetail();
		this._renderActions();
		if (this._editMode)
		{
			// Loaded on entering edit mode rather than on every view: these are real queries against Document.
			this.provider.loadDocumentCountsAsync().then(() => this._renderRail());
		}
	}

	/** Render the header action buttons (Edit / Done + Add State + Save + Discard + dirty badge). */
	_renderActions()
	{
		if (!this.pict.ContentAssignment.getElement('#ApprovalWorkflow-Actions')) { return; }
		const tmpEditor = this.editorState;
		const tmpCheckCount = (tmpEditor.Findings || []).filter((pFinding) => pFinding.Severity !== 'info').length;
		const tmpRecord =
		{
			CanEdit: !!tmpEditor.Editable,
			CheckSlot: tmpCheckCount ? [ { Count: tmpCheckCount, Plural: (tmpCheckCount === 1) ? '' : 's' } ] : [],
			JsonSlot: tmpEditor.Workflow ? [ {} ] : [],
			EditModeSlot: (tmpEditor.Editable && this._editMode) ? [ {} ] : [],
			ViewModeSlot: (tmpEditor.Editable && !this._editMode) ? [ {} ] : [],
			ReadOnlySlot: (!tmpEditor.Editable && !tmpEditor.Loading && !tmpEditor.Error) ? [ {} ] : [],
			DirtySlot: (tmpEditor.Editable && this._editMode && tmpEditor.Dirty) ? [ {} ] : [],
			DeleteSlot: (tmpEditor.Editable && this._editMode && !tmpEditor.IsNew && !tmpEditor.IDProject && tmpEditor.LevelInfo && !tmpEditor.LevelInfo.OnGlobal) ? [ {} ] : [],
		};
		this.pict.ContentAssignment.assignContent('#ApprovalWorkflow-Actions', this.pict.parseTemplateByHash('ApprovalWorkflow-Actions-Template', tmpRecord));
	}

	async addStateAndEdit()
	{
		const tmpCommit = await this._commitOpenPanel();
		if (!tmpCommit.OK)
		{
			return;
		}
		const tmpStatus = await this.provider.addStateAsync();
		this._refreshGraph();
		this._renderActions();
		this._setActiveState(tmpStatus);
		this._showStateEditor(tmpStatus);
	}

	async saveWorkflow()
	{
		const tmpCommit = await this._commitOpenPanel();
		if (!tmpCommit.OK)
		{
			return;
		}
		const tmpEditor = this.editorState;
		const tmpOptions = {};
		try
		{
			const tmpProblems = libModel.findSaveProblems(tmpEditor.Workflow);
			if (tmpProblems.length)
			{
				throw new Error(tmpProblems.join(' '));
			}
			if (tmpEditor.IsNew && !tmpEditor.Hash)
			{
				// A brand-new workflow needs a hash (the document-type key) — prompt for it.
				const tmpChoice = await this.modal.show(
					{
						title: 'Save new workflow',
						content: '<p>Enter a hash/name for this workflow (the key it is stored under):</p><input type="text" id="apw-new-hash" class="input" placeholder="e.g. MyChangeOrder" autofocus>',
						buttons: [ { Hash: 'cancel', Label: 'Cancel' }, { Hash: 'ok', Label: 'Save', Style: 'primary' } ],
					});
				if (tmpChoice !== 'ok')
				{
					return;
				}
				tmpOptions.Hash = ((document.getElementById('apw-new-hash') || {}).value || '').trim();
			}
			else if (!(await this._confirmSavePreview()))
			{
				return;
			}
			let tmpResult = await this.provider.saveAsync(tmpOptions);
			if (tmpResult.Conflict)
			{
				if (!(await this._confirmOverwrite(tmpResult.Changes)))
				{
					return;
				}
				tmpResult = await this.provider.saveAsync(Object.assign({}, tmpOptions, { Force: true }));
			}
			this._renderActions();
			this.modal.toast('Workflow saved.', { type: 'success' });
		}
		catch (pError)
		{
			this.modal.toast(`Save failed: ${pError && pError.message ? pError.message : pError}`, { type: 'error', duration: 8000 });
		}
	}

	/**
	 * Show what Save would change, and anything to check, and ask to go ahead.
	 * @return {Promise<Boolean>} true to save
	 */
	async _confirmSavePreview()
	{
		const tmpChanges = this.provider.pendingChanges();
		if (!tmpChanges.length)
		{
			this.modal.toast('There are no changes to save.', { type: 'info' });
			return false;
		}
		const tmpChecks = (this.editorState.Findings || []).filter((pFinding) => pFinding.Severity !== 'info');
		const tmpCheckHTML = tmpChecks.length
			? `<p class="mt-3"><strong>${tmpChecks.length} thing${(tmpChecks.length === 1) ? '' : 's'} to check</strong></p>${tmpChecks.map((pFinding) => `<div class="apwe-preview-line">${this._esc(pFinding.Message)}</div>`).join('')}`
			: '';
		const tmpChoice = await this.modal.show(
			{
				title: 'Save these changes?',
				width: 'min(720px, 92vw)',
				content: `<div class="apwe-preview">${tmpChanges.map((pLine) => `<div class="apwe-preview-line">${this._esc(pLine)}</div>`).join('')}${tmpCheckHTML}</div>`,
				buttons: [ { Hash: 'cancel', Label: 'Keep editing' }, { Hash: 'ok', Label: 'Save', Style: 'primary' } ],
			});
		return tmpChoice === 'ok';
	}

	/**
	 * Someone else saved this workflow first: show what they changed and ask whether to replace it.
	 * @param {Array<String>} pTheirChanges
	 * @return {Promise<Boolean>} true to replace their changes
	 */
	async _confirmOverwrite(pTheirChanges)
	{
		const tmpChoice = await this.modal.show(
			{
				title: 'Workflow changed',
				width: 'min(720px, 92vw)',
				content: `<p>Someone else changed this workflow after you opened it. Saving now replaces their changes with yours. To keep them, discard your edits and reopen the workflow.</p><p class="mt-3"><strong>What they changed</strong></p><div class="apwe-preview">${(pTheirChanges || []).map((pLine) => `<div class="apwe-preview-line">${this._esc(pLine)}</div>`).join('')}</div>`,
				buttons: [ { Hash: 'cancel', Label: 'Keep editing' }, { Hash: 'ok', Label: 'Replace their changes', Style: 'danger' } ],
			});
		return tmpChoice === 'ok';
	}

	/** List what's worth checking in the side panel. */
	showFindings()
	{
		const tmpFindings = (this.editorState.Findings || []).map((pFinding, pIndex) => ({
			Index: pIndex,
			Severity: pFinding.Severity,
			Kind: { error: 'Must fix', warning: 'Check', info: 'Note' }[pFinding.Severity],
			Message: this._esc(pFinding.Message),
		}));
		this._assignDetail(this.pict.parseTemplateByHash('ApprovalWorkflow-Findings-Template', { Findings: tmpFindings, EmptySlot: tmpFindings.length ? [] : [ {} ] }));
	}

	/** Go to the state or transition a finding concerns. */
	async openFinding(pIndex)
	{
		const tmpFinding = (this.editorState.Findings || [])[parseInt(pIndex, 10)];
		if (!tmpFinding || (tmpFinding.Status === undefined))
		{
			return;
		}
		this._setActiveState(tmpFinding.Status);
		if (this._editMode && (tmpFinding.TransitionIndex !== undefined))
		{
			this._showTransitionEditor(tmpFinding.Status, tmpFinding.TransitionIndex);
		}
		else if (this._editMode)
		{
			this._showStateEditor(tmpFinding.Status);
		}
		else
		{
			this._showStateDetail(tmpFinding.Status);
		}
	}

	/** See the workflow's full configuration; anyone who can edit it can also change it here. */
	async openJsonEditor()
	{
		const tmpCommit = await this._commitOpenPanel();
		if (!tmpCommit.OK)
		{
			return;
		}
		const tmpEditable = !!(this._editMode && this.editorState.Editable);
		let tmpText = JSON.stringify(this.provider.workflow, null, '\t');
		let tmpError = '';
		for (;;)
		{
			const tmpChoice = await this.modal.show(
				{
					title: tmpEditable ? 'Edit workflow JSON' : 'Workflow JSON',
					width: 'min(900px, 94vw)',
					content: `${tmpError ? `<p class="apwe-json-error">${this._esc(tmpError)}</p>` : ''}${tmpEditable ? '' : '<p class="is-size-7 has-text-grey mb-2">To change this, press Edit first.</p>'}<textarea id="apw-json-text" class="textarea apwe-json-text" spellcheck="false"${tmpEditable ? '' : ' readonly'}>${this._esc(tmpText)}</textarea>`,
					buttons: tmpEditable ? [ { Hash: 'cancel', Label: 'Cancel' }, { Hash: 'ok', Label: 'Apply to diagram', Style: 'primary' } ] : [ { Hash: 'cancel', Label: 'Close' } ],
				});
			if (tmpChoice !== 'ok')
			{
				return;
			}
			tmpText = (document.getElementById('apw-json-text') || {}).value || '';
			const tmpResult = await this.provider.applyWorkflowJSONAsync(tmpText);
			if (!tmpResult.Error)
			{
				if (tmpResult.Changed)
				{
					this._editRefStatus = null;
					this._editStateStatus = null;
					this._closeDetail();
					this._refreshGraph();
					this._renderActions();
					this.modal.toast('Applied to the diagram. Press Save to keep it.', { type: 'success' });
				}
				return;
			}
			tmpError = tmpResult.Error;
		}
	}

	/** Whether the open side panel holds typed values that haven't reached the working model. */
	hasPendingPanelEdits()
	{
		if (!this._editMode || (typeof document === 'undefined'))
		{
			return false;
		}
		if ((this._editRefStatus != null) && document.getElementById('apw-edit-action'))
		{
			return this.provider.transitionPatchChanges(this._editRefStatus, this._editRefIndex, this._transitionPanelValues());
		}
		if ((this._editStateStatus != null) && document.getElementById('apw-state-name'))
		{
			return this.provider.statePatchChanges(this._editStateStatus, this._statePanelValues());
		}
		return false;
	}

	_transitionPanelValues()
	{
		const tmpValue = (pID) => (document.getElementById(pID) || {}).value;
		const tmpChecked = (pID) => !!(document.getElementById(pID) || {}).checked;
		return {
			Action: tmpValue('apw-edit-action'),
			ToStatus: tmpValue('apw-edit-target'),
			LockEntity: tmpChecked('apw-edit-lock'),
			UnlockEntity: tmpChecked('apw-edit-unlock'),
			IsApproval: tmpChecked('apw-edit-approval'),
			AllowOnLocked: tmpChecked('apw-edit-allowlocked'),
		};
	}

	_statePanelValues()
	{
		return {
			StateName: (document.getElementById('apw-state-name') || {}).value,
			Status: (document.getElementById('apw-state-status') || {}).value,
		};
	}

	/**
	 * Apply the state panel, asking first if the rename would strand documents.
	 * @return {Promise<{ OK: Boolean, RenamedFrom?: String, RenamedTo?: String, Changed?: Boolean }>}
	 */
	async _applyStatePanel()
	{
		const tmpPrior = this._editStateStatus;
		const tmpValues = this._statePanelValues();
		const tmpCount = this.provider.documentCountFor(tmpPrior);
		if ((String(tmpValues.Status || '').trim() !== tmpPrior) && tmpCount)
		{
			const tmpOk = await this.modal.confirm(`At least ${tmpCount.toLocaleString()} document${(tmpCount === 1) ? ' is' : 's are'} in "${tmpPrior}". They keep that hash and can't be acted on after a rename.`, { title: 'Rename state', confirmLabel: 'Rename anyway', cancelLabel: 'Keep the hash', dangerous: true });
			if (!tmpOk)
			{
				return { OK: false };
			}
		}
		const tmpResult = await this.provider.applyStateEditAsync(tmpPrior, tmpValues);
		if (tmpResult && tmpResult.Error)
		{
			this.modal.toast(tmpResult.Error, { type: 'error', duration: 5000 });
			return { OK: false };
		}
		if (tmpResult && tmpResult.Changed)
		{
			this._editStateStatus = tmpResult.Status;
			this._refreshGraph();
			this._renderActions();
		}
		return { OK: true, RenamedFrom: tmpPrior, RenamedTo: tmpResult ? tmpResult.Status : tmpPrior, Changed: !!(tmpResult && tmpResult.Changed) };
	}

	/**
	 * Put whatever is typed into the open side panel onto the working model, so leaving the panel or pressing Save can't
	 * drop it. OK is false when the panel holds a value that can't be applied; the panel stays open and says why.
	 * @return {Promise<{ OK: Boolean, RenamedFrom?: String, RenamedTo?: String }>}
	 */
	async _commitOpenPanel()
	{
		if (!this._editMode || (typeof document === 'undefined'))
		{
			return { OK: true };
		}
		if (await this._commitFields())
		{
			this._refreshGraph();
			this._renderActions();
		}
		if (!document.getElementById('apw-state-name') || (this._editStateStatus == null))
		{
			return { OK: true };
		}
		return this._applyStatePanel();
	}

	async discardChanges()
	{
		if (this.editorState.Dirty)
		{
			const tmpOk = await this.modal.confirm('Discard all unsaved changes to this workflow?', { title: 'Discard changes', confirmLabel: 'Discard', cancelLabel: 'Keep editing', dangerous: true });
			if (!tmpOk) { return; }
		}
		await this.provider.discardAsync();
		this._refreshGraph();
		this._closeDetail();
		this._renderActions();
	}

	// --- Detail / edit panels ------------------------------------------------------------------------

	_assignDetail(pHTML)
	{
		if (this.pict.ContentAssignment.getElement('#ApprovalWorkflow-Detail'))
		{
			this.pict.ContentAssignment.assignContent('#ApprovalWorkflow-Detail', pHTML);
		}
	}

	_showTransitionDetail(pData)
	{
		this._assignDetail(this.pict.parseTemplateByHash('ApprovalWorkflow-Detail-Template',
			{
				SourceStateName: pData.SourceStateName || pData.SourceStatus || '',
				ToStateName: pData.ToStateName || pData.ToStatus || '',
				SentenceText: pData.SentenceText || '',
				BranchSlot: pData.IsBranch ? [ {} ] : [],
				OtherSlot: pData.OtherSlot || [],
			}));
	}

	_showBranchReadonly(pData)
	{
		this._assignDetail(this.pict.parseTemplateByHash('ApprovalWorkflow-BranchReadonly-Template',
			{ SourceStateName: pData.SourceStateName || pData.SourceStatus || '', ToStateName: pData.ToStateName || pData.ToStatus || '', SentenceText: pData.SentenceText || '' }));
	}

	/** Read-only summary of a state (view mode): its identity + in/out counts + clickable outgoing transitions. */
	_stateDetailRecord(pStatus)
	{
		const tmpStates = (this.provider.workflow && this.provider.workflow.States) || [];
		const tmpState = tmpStates.find((pState) => pState.Status === pStatus);
		if (!tmpState) { return null; }
		const tmpNameOf = (pTarget) => { const tmpFound = tmpStates.find((pState) => pState.Status === pTarget); return tmpFound ? (tmpFound.StateName || tmpFound.Status) : pTarget; };
		const tmpTransitions = (tmpState.Transitions || []).map((pTransition, pIndex) => ({
			Status: this._esc(pStatus), TransitionIndex: pIndex, Action: this._esc(pTransition.Action || '(no action)'), ToStateName: this._esc(tmpNameOf(pTransition.ToStatus)),
		}));
		let tmpIncoming = 0;
		tmpStates.forEach((pState) => (pState.Transitions || []).forEach((pTransition) => { if (pTransition.ToStatus === pStatus) { tmpIncoming++; } }));
		return {
			Status: pStatus,
			StateName: tmpState.StateName || pStatus,
			Transitions: tmpTransitions,
			EmptySlot: tmpTransitions.length ? [] : [ {} ],
			IncomingText: `${tmpIncoming} in`,
			OutgoingText: `${tmpTransitions.length} out`,
			EditHintSlot: this.editorState.Editable ? [ {} ] : [],
		};
	}

	_showStateDetail(pStatus)
	{
		const tmpRecord = this._stateDetailRecord(pStatus);
		if (!tmpRecord) { this._closeDetail(); return; }
		this._assignDetail(this.pict.parseTemplateByHash('ApprovalWorkflow-StateDetail-Template', tmpRecord));
	}

	/** A clicked edge's detail, plus what else its transition carries. */
	_transitionDetailRecord(pInfo)
	{
		const tmpDetail = this.provider.edgeDetail(pInfo);
		if (!tmpDetail)
		{
			return null;
		}
		const tmpState = ((this.provider.workflow && this.provider.workflow.States) || []).find((pState) => pState.Status === pInfo.SourceStatus);
		const tmpTransition = tmpState && (tmpState.Transitions || [])[pInfo.TransitionIndex];
		return Object.assign({}, tmpDetail, { OtherSlot: (tmpTransition && !pInfo.IsBranch) ? this._otherSettingsSlot(tmpTransition) : [] });
	}

	/** From a read-only state panel, open a transition's read-only sentence detail. */
	viewTransitionDetail(pStatus, pIndex)
	{
		const tmpIndex = parseInt(pIndex, 10);
		const tmpStates = (this.provider.workflow && this.provider.workflow.States) || [];
		const tmpState = tmpStates.find((pState) => pState.Status === pStatus);
		const tmpTransition = tmpState && (tmpState.Transitions || [])[tmpIndex];
		if (!tmpTransition) { return; }
		const tmpDetail = this._transitionDetailRecord({ SourceStatus: pStatus, TransitionIndex: tmpIndex, IsBranch: false, ToStatus: tmpTransition.ToStatus });
		if (tmpDetail) { this._showTransitionDetail(tmpDetail); }
	}

	/** Look up a transition's current values from the working model (index is stable across edits). */
	_transitionRecord(pStatus, pIndex)
	{
		const tmpStates = (this.provider.workflow && this.provider.workflow.States) || [];
		const tmpState = tmpStates.find((pState) => pState.Status === pStatus);
		if (!tmpState) { return null; }
		const tmpTransition = (tmpState.Transitions || [])[pIndex];
		if (!tmpTransition) { return null; }
		const tmpOptions = this.provider.stateOptions().map((pOption) => ({ Status: this._esc(pOption.Status), StateName: this._esc(pOption.StateName || pOption.Status), SelectedAttr: (pOption.Status === tmpTransition.ToStatus) ? 'selected' : '' }));
		// A target that names no state stays selected; the first option would otherwise silently take its place.
		if (!this.provider.stateOptions().some((pOption) => pOption.Status === tmpTransition.ToStatus))
		{
			const tmpTarget = (tmpTransition.ToStatus == null) ? '' : String(tmpTransition.ToStatus);
			tmpOptions.unshift({ Status: this._esc(tmpTarget), StateName: this._esc(tmpTarget ? `${tmpTarget} (missing state)` : '(not set)'), SelectedAttr: 'selected' });
		}
		const tmpActors = this.provider.actorLabels(pStatus, pIndex).map((pRow) => Object.assign({}, pRow, { Label: this._esc(pRow.Label) }));
		return {
			SourceStatus: pStatus,
			SourceStateName: tmpState.StateName || pStatus,
			TransitionIndex: pIndex,
			Action: this._esc(tmpTransition.Action || ''),
			Options: tmpOptions,
			LockChecked: tmpTransition.LockEntity ? 'checked' : '',
			UnlockChecked: tmpTransition.UnlockEntity ? 'checked' : '',
			ApprovalChecked: tmpTransition.IsApproval ? 'checked' : '',
			AllowLockedChecked: tmpTransition.AllowOnLocked ? 'checked' : '',
			Actors: tmpActors,
			ActorEmptySlot: tmpActors.length ? [] : [ {} ],
			OtherSlot: this._otherSettingsSlot(tmpTransition),
		};
	}

	/**
	 * The "Other settings" section for a transition: one record holding its rows, or nothing when it has none.
	 * @param {Object} pTransition
	 * @return {Array<Object>}
	 */
	_otherSettingsSlot(pTransition)
	{
		const tmpRows = libModel.otherTransitionSettings(pTransition, libModel.buildStateMap(this.provider.workflow));
		return tmpRows.length ? [ { Rows: tmpRows.map((pRow) => ({ Label: this._esc(pRow.Label), Detail: this._esc(pRow.Detail) })) } ] : [];
	}

	_showTransitionEditor(pStatus, pIndex)
	{
		const tmpRecord = this._transitionRecord(pStatus, pIndex);
		if (!tmpRecord) { this._closeDetail(); return; }
		this._editRefStatus = pStatus;
		this._editRefIndex = pIndex;
		this._assignDetail(this.pict.parseTemplateByHash('ApprovalWorkflow-TransitionEditor-Template', tmpRecord));
	}

	async applyTransitionForm()
	{
		const tmpValue = (pID) => (document.getElementById(pID) || {}).value;
		const tmpAction = (tmpValue('apw-edit-action') || '').trim();
		const tmpTargetStatus = tmpValue('apw-edit-target');
		await this._commitFields();
		this._refreshGraph();
		this._renderActions();
		this._showTransitionEditor(this._editRefStatus, this._editRefIndex);
		// Make it obvious what the Apply just did to the diagram (and that it still needs saving).
		const tmpTargetName = (this.provider.stateOptions().find((pOption) => pOption.Status === tmpTargetStatus) || {}).StateName || tmpTargetStatus || '(unset)';
		const tmpSourceName = (this.provider.stateOptions().find((pOption) => pOption.Status === this._editRefStatus) || {}).StateName || this._editRefStatus;
		this.modal.toast(`Applied to the diagram: ${tmpSourceName} — '${tmpAction || 'no action'}' → ${tmpTargetName}. Press Save to keep it.`, { type: 'success' });
	}

	async deleteTransitionFromForm()
	{
		const tmpStatus = this._editRefStatus;
		const tmpOk = await this.modal.confirm('Delete this transition, including any conditional branches, triggers and other settings it carries?', { title: 'Delete transition', confirmLabel: 'Delete', cancelLabel: 'Cancel', dangerous: true });
		if (!tmpOk)
		{
			return;
		}
		await this.provider.removeTransitionAsync(tmpStatus, this._editRefIndex);
		this._refreshGraph();
		this._renderActions();
		this._showStateEditor(tmpStatus);
	}

	// --- P3 actor editing --------------------------------------------------------------------------------

	/**
	 * Commit the transition editor's field inputs into the working model.
	 * @return {Promise<Boolean>} true if anything changed
	 */
	async _commitFields()
	{
		if (this._editRefStatus == null || !document.getElementById('apw-edit-action'))
		{
			return false;
		}
		return this.provider.applyTransitionEditAsync(this._editRefStatus, this._editRefIndex, this._transitionPanelValues());
	}

	async removeActor(pActorIndex, pSettingIndex)
	{
		await this._commitFields();
		await this.provider.removeActorAsync(this._editRefStatus, this._editRefIndex, parseInt(pActorIndex, 10), parseInt(pSettingIndex, 10));
		this._refreshGraph();
		this._renderActions();
		this._showTransitionEditor(this._editRefStatus, this._editRefIndex);
	}

	_esc(pValue)
	{
		return String(pValue == null ? '' : pValue).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
	}

	async openAddActorModal()
	{
		await this._commitFields();
		await this.provider.loadCustomerRolesAsync();
		// Captured add-actor state (read on OK; the modal DOM is gone by then).
		this._addActor = { Type: 'ProjectRole', Role: (this.provider.customerRoles[0] || {}).PermissionLevel || '', Level: '3', User: null };
		this._userResults = [];
		this._actorTagOptions = libModel.actorTagOptions(this.provider.workflow);
		const tmpChoice = await this.modal.show(
			{
				title: 'Add actor',
				content: this._addActorModalContent(),
				buttons: [ { Hash: 'cancel', Label: 'Cancel' }, { Hash: 'ok', Label: 'Add', Style: 'primary' } ],
			});
		if (tmpChoice !== 'ok') { return; }
		this._addActor.Tags = this._readTagInput();
		const tmpSpec = this._buildActorSpec();
		if (!tmpSpec) { this.modal.toast('Choose a value for that actor type.', { type: 'warning' }); return; }
		await this.provider.addActorAsync(this._editRefStatus, this._editRefIndex, tmpSpec);
		this._refreshGraph();
		this._renderActions();
		this._showTransitionEditor(this._editRefStatus, this._editRefIndex);
	}

	_addActorModalContent()
	{
		const tmpTypes =
		[
			[ 'ProjectRole', 'Users with a project role' ],
			[ 'UserRole', 'Users with a role' ],
			[ 'UserRoleProjectMembership', 'Users with a role who are project members' ],
			[ 'FormLabRole', 'Users in the form\'s lab with a role' ],
			[ 'ProjectEntitlement', 'Users with a project entitlement' ],
			[ 'FormLabUser', 'Everyone in the form\'s lab' ],
			[ 'FormMaterialLabRole', 'Users assigned to the form\'s lab and material, with a role' ],
			[ 'FormMaterialLabInitialUser', 'First-contact users for the form\'s lab and material' ],
			[ 'FormMaterialLabEscalationUser', 'Escalation users for the form\'s lab and material' ],
			[ 'Users', 'A specific user' ],
			[ 'Admin', 'Administrators' ],
			[ 'CreatingUser', 'The user who created the entity' ],
			[ 'HigherUserLevelInProject', 'Higher-level users in the project' ],
			[ 'MinimumUserLevelInProject', 'Users at least a minimum level' ],
		];
		const tmpTypeOptions = tmpTypes.map((pType) => `<option value="${pType[0]}">${this._esc(pType[1])}</option>`).join('');
		// Roles use a SEARCHABLE, scrollable list (not a long <select>) so an admin can find the right role fast.
		// The full set is already loaded (customerRoles), so the search filters it in-memory; `_roleResults` tracks
		// the currently-shown (filtered) list that `_chooseRoleForActor` indexes into.
		this._roleResults = (this.provider.customerRoles || []).slice();
		const tmpRoleRows = this._roleRowsHTML(this._roleResults, this._addActor && this._addActor.Role);
		const tmpRoleChosen = this._roleChosenText(this._addActor && this._addActor.Role);
		return /*html*/`
			<div class="apwe-addactor">
				<label>Actor type</label>
				<div class="select is-small is-fullwidth"><select id="apw-actor-type" onchange="_Pict.views.ApprovalWorkflowEditor._setActorType(this.value)">${tmpTypeOptions}</select></div>
				<div id="apw-actor-role-wrap">
					<label>Role</label>
					<input type="text" class="input is-small" placeholder="Search roles…" oninput="_Pict.views.ApprovalWorkflowEditor._searchRolesForActor(this.value)">
					<div class="apwe-user-results" id="apw-actor-role-results">${tmpRoleRows}</div>
					<p class="is-size-7 has-text-grey" id="apw-actor-role-chosen">${tmpRoleChosen}</p>
				</div>
				<div id="apw-actor-level-wrap" style="display:none"><label>Minimum level</label><input type="number" id="apw-actor-level" class="input is-small" value="3" min="0" oninput="_Pict.views.ApprovalWorkflowEditor._setActorLevel(this.value)"></div>
				<div id="apw-actor-user-wrap" style="display:none">
					<label>Find user</label>
					<input type="text" class="input is-small" placeholder="Name, login ID or email…" oninput="_Pict.views.ApprovalWorkflowEditor._searchUsersForActor(this.value)">
					<div class="apwe-user-results" id="apw-actor-user-results"></div>
					<p class="is-size-7 has-text-grey" id="apw-actor-user-chosen">No user selected.</p>
				</div>
				<div id="apw-actor-keyword-wrap" style="display:none"><p class="is-size-7 has-text-grey mt-2">This actor type needs no further selection.</p></div>
				${this._tagInputHTML('')}
			</div>`;
	}

	_setActorType(pType)
	{
		this._addActor.Type = pType;
		const tmpRoleTypes = libModel.ROLE_ACTOR_TYPES;
		const tmpShow = (pID, pOn) => { const tmpEl = document.getElementById(pID); if (tmpEl) { tmpEl.style.display = pOn ? '' : 'none'; } };
		tmpShow('apw-actor-role-wrap', tmpRoleTypes.indexOf(pType) > -1);
		tmpShow('apw-actor-level-wrap', pType === 'MinimumUserLevelInProject');
		tmpShow('apw-actor-user-wrap', pType === 'Users');
		tmpShow('apw-actor-keyword-wrap', (libModel.KEYWORD_ACTOR_TYPES.indexOf(pType) > -1) && (pType !== 'MinimumUserLevelInProject'));
	}

	_setActorRole(pLevel) { this._addActor.Role = pLevel; }
	_setActorLevel(pValue) { this._addActor.Level = pValue; }

	async _searchUsersForActor(pQuery)
	{
		this._userResults = await this.provider.searchUsersAsync(pQuery);
		const tmpHTML = this._userResults.length
			? this._userResults.map((pUser, pIndex) => `<a class="panel-block is-size-7" onclick="_Pict.views.ApprovalWorkflowEditor._pickUser(${pIndex})">${this._esc(pUser.Name)}</a>`).join('')
			: '<p class="is-size-7 has-text-grey">No matches.</p>';
		if (document.getElementById('apw-actor-user-results'))
		{
			this.pict.ContentAssignment.assignContent('#apw-actor-user-results', tmpHTML);
		}
	}

	_pickUser(pIndex)
	{
		this._addActor.User = this._userResults[parseInt(pIndex, 10)] || null;
		const tmpEl = document.getElementById('apw-actor-user-chosen');
		if (tmpEl) { tmpEl.textContent = this._addActor.User ? `Selected: ${this._addActor.User.Name}` : 'No user selected.'; }
	}

	/** Render the (filtered) role list as clickable rows, highlighting the chosen one (mirrors the user results). */
	_roleRowsHTML(pRoles, pSelectedLevel)
	{
		if (!Array.isArray(pRoles) || !pRoles.length) { return '<p class="is-size-7 has-text-grey p-2">No matching roles.</p>'; }
		const tmpSelected = (pSelectedLevel == null) ? '' : String(pSelectedLevel);
		return pRoles.map((pRole, pIndex) =>
			`<a class="panel-block is-size-7${String(pRole.PermissionLevel) === tmpSelected ? ' has-text-link has-text-weight-semibold' : ''}" onclick="_Pict.views.ApprovalWorkflowEditor._chooseRoleForActor(${pIndex})">${this._esc(pRole.Name)}</a>`).join('');
	}

	/** The "Selected: …" line for the chosen role level (name resolved from customerRoles). */
	_roleChosenText(pSelectedLevel)
	{
		if (pSelectedLevel == null || pSelectedLevel === '') { return 'No role selected.'; }
		const tmpRole = (this.provider.customerRoles || []).find((pRole) => String(pRole.PermissionLevel) === String(pSelectedLevel));
		return `Selected: ${this._esc(tmpRole ? tmpRole.Name : pSelectedLevel)}`;
	}

	/** Filter the (already-loaded) roles client-side by name or permission level and repaint the results list. */
	_searchRolesForActor(pQuery)
	{
		const tmpQuery = String(pQuery || '').trim().toLowerCase();
		const tmpAll = this.provider.customerRoles || [];
		this._roleResults = tmpQuery
			? tmpAll.filter((pRole) => (String(pRole.Name || '').toLowerCase().indexOf(tmpQuery) > -1) || (String(pRole.PermissionLevel || '').toLowerCase().indexOf(tmpQuery) > -1))
			: tmpAll.slice();
		if (document.getElementById('apw-actor-role-results'))
		{
			this.pict.ContentAssignment.assignContent('#apw-actor-role-results', this._roleRowsHTML(this._roleResults, this._addActor && this._addActor.Role));
		}
	}

	/** Choose a role from the (filtered) list: capture its permission level + update the chosen line + highlight. */
	_chooseRoleForActor(pIndex)
	{
		const tmpRole = (this._roleResults || [])[parseInt(pIndex, 10)] || null;
		if (!tmpRole) { return; }
		this._addActor.Role = tmpRole.PermissionLevel;
		const tmpChosen = document.getElementById('apw-actor-role-chosen');
		if (tmpChosen) { tmpChosen.textContent = `Selected: ${tmpRole.Name}`; }
		if (document.getElementById('apw-actor-role-results'))
		{
			this.pict.ContentAssignment.assignContent('#apw-actor-role-results', this._roleRowsHTML(this._roleResults, this._addActor.Role));
		}
	}

	/** Build the actor spec (object for role/user, string for keyword/level unless tagged) from the captured modal state. */
	_buildActorSpec()
	{
		const tmpState = this._addActor || {};
		const tmpTags = tmpState.Tags || [];
		if (libModel.ROLE_ACTOR_TYPES.indexOf(tmpState.Type) > -1)
		{
			return tmpState.Role ? { Type: tmpState.Type, Hashes: [ String(tmpState.Role) ], Tags: tmpTags } : null;
		}
		if (tmpState.Type === 'Users')
		{
			return tmpState.User ? { Type: 'Users', Hashes: [ String(tmpState.User.IDUser) ], Tags: tmpTags } : null;
		}
		if (tmpState.Type === 'MinimumUserLevelInProject')
		{
			const tmpLevel = parseInt(tmpState.Level, 10);
			if (isNaN(tmpLevel))
			{
				return null;
			}
			return tmpTags.length ? { Type: tmpState.Type, Hashes: [ String(tmpLevel) ], Tags: tmpTags } : `MinimumUserLevelInProject:${tmpLevel}`;
		}
		return tmpTags.length ? { Type: tmpState.Type, Tags: tmpTags } : tmpState.Type;
	}

	/** The tag field shared by the add-actor and set-tags modals. */
	_tagInputHTML(pValue)
	{
		const tmpOptions = (this._actorTagOptions || []).map((pTag) => `<option value="${this._esc(pTag)}"></option>`).join('');
		return `<label>Tags <span class="has-text-weight-normal">(optional, separate with commas)</span></label><input type="text" id="apw-actor-tags" class="input is-small" list="apw-actor-tag-options" value="${this._esc(pValue)}" placeholder="e.g. Primary"><datalist id="apw-actor-tag-options">${tmpOptions}</datalist><p class="is-size-7 has-text-grey mt-1">Tags decide who gets prompted and notified. They don't change who can act. Usual ones: ${this._esc((this._actorTagOptions || []).join(', '))}.</p>`;
	}

	_readTagInput()
	{
		return libModel.parseActorTags((document.getElementById('apw-actor-tags') || {}).value, this._actorTagOptions);
	}

	/** Change the tags on one actor row. */
	async openActorTagsModal(pActorIndex, pSettingIndex)
	{
		await this._commitFields();
		const tmpRow = this.provider.actorLabels(this._editRefStatus, this._editRefIndex).find((pCandidate) => (pCandidate.ActorIndex === pActorIndex) && (pCandidate.SettingIndex === pSettingIndex));
		if (!tmpRow)
		{
			return;
		}
		this._actorTagOptions = libModel.actorTagOptions(this.provider.workflow);
		const tmpChoice = await this.modal.show(
			{
				title: 'Set tags',
				content: `<div class="apwe-addactor"><p class="is-size-7 mb-2">${this._esc(tmpRow.Label)}</p>${this._tagInputHTML(tmpRow.Tags.join(', '))}</div>`,
				buttons: [ { Hash: 'cancel', Label: 'Cancel' }, { Hash: 'ok', Label: 'Apply', Style: 'primary' } ],
			});
		if (tmpChoice !== 'ok')
		{
			return;
		}
		await this.provider.setActorTagsAsync(this._editRefStatus, this._editRefIndex, pActorIndex, pSettingIndex, this._readTagInput());
		this._refreshGraph();
		this._renderActions();
		this._showTransitionEditor(this._editRefStatus, this._editRefIndex);
	}

	_stateEditorRecord(pStatus)
	{
		const tmpStates = (this.provider.workflow && this.provider.workflow.States) || [];
		const tmpState = tmpStates.find((pState) => pState.Status === pStatus);
		if (!tmpState) { return null; }
		const tmpTransitions = (tmpState.Transitions || []).map((pTransition, pIndex) => ({
			Status: this._esc(pStatus), TransitionIndex: pIndex, Action: this._esc(pTransition.Action || '(no action)'), ToStatus: this._esc(pTransition.ToStatus || '(unset)'),
		}));
		return {
			Status: this._esc(pStatus),
			StateName: this._esc(tmpState.StateName || ''),
			Transitions: tmpTransitions,
			EmptySlot: tmpTransitions.length ? [] : [ {} ],
		};
	}

	_showStateEditor(pStatus)
	{
		const tmpRecord = this._stateEditorRecord(pStatus);
		if (!tmpRecord) { this._closeDetail(); return; }
		this._editStateStatus = pStatus;
		this._assignDetail(this.pict.parseTemplateByHash('ApprovalWorkflow-StateEditor-Template', tmpRecord));
	}

	async applyStateForm()
	{
		const tmpValues = this._statePanelValues();
		const tmpResult = await this._applyStatePanel();
		if (!tmpResult.OK)
		{
			return;
		}
		this._showStateEditor(this._editStateStatus);
		this.modal.toast(`Applied to the diagram: state '${tmpValues.StateName || tmpValues.Status}'. Press Save to keep it.`, { type: 'success' });
	}

	async addTransitionFromState()
	{
		const tmpCommit = await this._commitOpenPanel();
		if (!tmpCommit.OK)
		{
			return;
		}
		const tmpStatus = this._editStateStatus;
		const tmpIndex = await this.provider.addTransitionAsync(tmpStatus);
		this._refreshGraph();
		this._renderActions();
		this._showTransitionEditor(tmpStatus, tmpIndex);
	}

	async openTransitionEditor(pStatus, pIndex)
	{
		const tmpCommit = await this._commitOpenPanel();
		if (!tmpCommit.OK)
		{
			return;
		}
		this._showTransitionEditor((tmpCommit.RenamedFrom === pStatus) ? tmpCommit.RenamedTo : pStatus, parseInt(pIndex, 10));
	}

	async deleteStateFromForm()
	{
		const tmpStatus = this._editStateStatus;
		const tmpCount = this.provider.documentCountFor(tmpStatus);
		const tmpDocuments = tmpCount ? ` At least ${tmpCount.toLocaleString()} document${(tmpCount === 1) ? ' is' : 's are'} in this state and can't be acted on afterwards.` : '';
		const tmpOk = await this.modal.confirm(`Delete the state "${tmpStatus}" and every transition into it?${tmpDocuments}`, { title: 'Delete state', confirmLabel: 'Delete', cancelLabel: 'Cancel', dangerous: true });
		if (!tmpOk) { return; }
		await this.provider.removeStateAsync(tmpStatus);
		this._editStateStatus = null;
		this._refreshGraph();
		this._renderActions();
		this._closeDetail();
	}

	async closeDetail()
	{
		const tmpCommit = await this._commitOpenPanel();
		if (!tmpCommit.OK)
		{
			return;
		}
		this._editRefStatus = null;
		this._editStateStatus = null;
		this._closeDetail();
	}

	_closeDetail()
	{
		this._assignDetail('');
	}
}

module.exports = ApprovalWorkflowEditor;

module.exports.default_configuration =
{
	ViewIdentifier: 'ApprovalWorkflowEditor',
	DefaultRenderable: 'ApprovalWorkflowEditorWrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,

	CSS: /*css*/`
		.apwe-wrap { display: flex; flex-direction: column; height: calc(100vh - 3.25rem); padding: 1rem 1.25rem 1rem; box-sizing: border-box; }
		.apwe-header { flex-shrink: 0; display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; margin-bottom: 0.75rem; }
		.apwe-crumbs { font-size: 0.8rem; margin-bottom: 0.2rem; }
		.apwe-crumbs a { color: var(--bulma-text-weak, #7a8598); font-weight: 600; text-decoration: none; }
		.apwe-crumbs a:hover { color: var(--bulma-link, #156dd1); }
		.apwe-title { font-weight: 700; font-size: 1.35rem; word-break: break-all; display: flex; align-items: center; gap: 0.5rem; }
		.apwe-title .icon { color: var(--bulma-link, #156dd1); }
		.apwe-actions { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; justify-content: flex-end; }
		.apwe-badge { display: inline-flex; align-items: center; gap: 0.35rem; font-size: 0.75rem; padding: 0.2rem 0.6rem; border-radius: 999px; background: var(--bulma-scheme-main-bis, #f2f4f8); color: var(--bulma-text-weak, #7a7a7a); border: 1px solid var(--bulma-border, #e6e9ef); }
		.apwe-dirty { display: inline-flex; align-items: center; gap: 0.35rem; color: #b8860b; font-size: 0.78rem; font-weight: 600; }
		.apwe-dirty .icon { font-size: 0.5rem; animation: apwe-pulse 1.6s ease-in-out infinite; }
		@keyframes apwe-pulse { 50% { opacity: 0.35; } }

		/* --- Body: steps rail + canvas side by side. --- */
		.apwe-body { flex: 1; min-height: 0; display: flex; gap: 0.75rem; }

		/* --- Steps rail: the ordered state list beside the diagram. --- */
		.apwe-rail { width: 15rem; flex-shrink: 0; display: flex; flex-direction: column; border: 1px solid var(--bulma-border, #e2e6ee); border-radius: 12px; background: var(--bulma-scheme-main, #fff); overflow: hidden; transition: width 0.16s ease; }
		.apwe-rail.is-collapsed { width: 2.5rem; }
		.apwe-rail.is-collapsed .apwe-rail-title, .apwe-rail.is-collapsed .apwe-rail-count, .apwe-rail.is-collapsed .apwe-steps, .apwe-rail.is-collapsed .apwe-rail-add { display: none; }
		.apwe-rail.is-collapsed .apwe-rail-head { justify-content: center; }
		.apwe-rail.is-collapsed .apwe-rail-toggle i { transform: rotate(180deg); }
		.apwe-rail > #ApprovalWorkflow-Rail { display: flex; flex-direction: column; min-height: 0; flex: 1; }
		.apwe-rail-head { display: flex; align-items: center; gap: 0.4rem; padding: 0.6rem 0.5rem 0.6rem 0.75rem; border-bottom: 1px solid var(--bulma-border, #eef0f4); flex-shrink: 0; }
		.apwe-rail-title { font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--bulma-text-weak, #9aa3b2); }
		.apwe-rail-count { font-size: 0.68rem; font-weight: 700; color: var(--bulma-link, #156dd1); background: #eef3fb; border-radius: 999px; padding: 0.05rem 0.45rem; }
		.apwe-rail-toggle { margin-left: auto; border: none; background: transparent; color: var(--bulma-text-weak, #9aa3b2); cursor: pointer; padding: 0.2rem 0.3rem; border-radius: 5px; font-size: 0.75rem; }
		.apwe-rail-toggle:hover { background: var(--bulma-scheme-main-bis, #f1f4f9); color: var(--bulma-link, #156dd1); }
		.apwe-steps { overflow-y: auto; padding: 0.4rem; flex: 1; min-height: 0; }
		.apwe-step { display: flex; align-items: center; gap: 0.55rem; padding: 0.4rem 0.5rem; border-radius: 8px; cursor: pointer; border: 1px solid transparent; transition: background 0.1s, border-color 0.1s, box-shadow 0.1s, opacity 0.1s; }
		.apwe-step:hover { background: var(--bulma-scheme-main-bis, #f4f6fa); }
		.apwe-step.is-active { background: #eef4fd; border-color: #cfe0f6; }
		.apwe-step-dot { flex-shrink: 0; width: 0.85rem; height: 0.85rem; border-radius: 50%; border: 2px solid #999; }
		.apwe-step-main { min-width: 0; }
		.apwe-step-name { font-size: 0.82rem; font-weight: 600; color: #22334c; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
		.apwe-step.is-active .apwe-step-name { color: var(--bulma-link, #156dd1); }
		.apwe-step-sub { font-size: 0.68rem; color: var(--bulma-text-weak, #9aa3b2); }
		.apwe-rail-empty { padding: 1rem 0.6rem; }
		.apwe-rail-add { display: flex; align-items: center; justify-content: center; gap: 0.4rem; margin: 0.4rem; padding: 0.5rem; flex-shrink: 0;
			border: 1px dashed var(--bulma-border, #cdd5e0); border-radius: 8px; background: transparent; color: var(--bulma-link, #156dd1); cursor: pointer; font-weight: 600; font-size: 0.8rem; }
		.apwe-rail-add:hover { background: #eef4fd; border-color: var(--bulma-link, #156dd1); }

		/* --- The graph canvas: a real diagramming surface (dot grid, inset depth). --- */
		.apwe-flow-host { flex: 1; min-height: 0; position: relative; border: 1px solid var(--bulma-border, #e2e6ee); border-radius: 12px; overflow: hidden;
			background-color: var(--bulma-scheme-main-bis, #fbfcfe);
			background-image: radial-gradient(rgba(99,115,145,0.18) 1px, transparent 1px);
			background-size: 22px 22px; background-position: -1px -1px;
			box-shadow: inset 0 1px 3px rgba(20,40,80,0.05); }
		#ApprovalWorkflow-Flow-Container { position: absolute; inset: 0; overflow: hidden; }
		#ApprovalWorkflow-Flow-Container svg { width: 100%; height: 100%; }
		/* graphviz emits <g class="node"> / <g class="edge"> — depth + clickable feel + hover lift. */
		#ApprovalWorkflow-Flow-Container .node, #ApprovalWorkflow-Flow-Container .edge { cursor: pointer; }
		#ApprovalWorkflow-Flow-Container .node { filter: drop-shadow(0 1px 2px rgba(20,40,80,0.16)); transition: filter 0.12s ease; }
		#ApprovalWorkflow-Flow-Container .node:hover { filter: drop-shadow(0 4px 8px rgba(20,40,80,0.26)); }
		#ApprovalWorkflow-Flow-Container .node:hover > text { font-weight: 700; }
		#ApprovalWorkflow-Flow-Container .edge > path { transition: stroke-width 0.12s ease; }
		#ApprovalWorkflow-Flow-Container .edge:hover > path { stroke-width: 3; }
		#ApprovalWorkflow-Flow-Container .edge:hover > text { font-weight: 700; }
		/* Node selected from the steps rail (or by clicking it): blue glow + heavier border. */
		#ApprovalWorkflow-Flow-Container .node.apw-selected { filter: drop-shadow(0 0 0 2px rgba(21,109,209,0.9)) drop-shadow(0 4px 10px rgba(21,109,209,0.35)); }
		#ApprovalWorkflow-Flow-Container .node.apw-selected > path { stroke-width: 3.4; }
		#ApprovalWorkflow-Flow-Container .node.apw-selected > text { font-weight: 700; }
		.apw-graph-error { padding: 2.5rem 1rem; color: var(--bulma-text-weak, #7a7a7a); text-align: center; }
		.apwe-overlay { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; z-index: 5; background: var(--bulma-scheme-main, #fff); }
		.apwe-spinner { display: inline-block; width: 1.8rem; height: 1.8rem; border: 3px solid var(--bulma-border, #dbdbdb); border-top-color: var(--bulma-link, #156dd1); border-radius: 50%; animation: apwe-spin 0.8s linear infinite; }
		@keyframes apwe-spin { to { transform: rotate(360deg); } }

		/* --- Floating on-canvas controls: zoom cluster + colour legend. --- */
		.apwe-zoom { position: absolute; right: 0.85rem; bottom: 0.85rem; z-index: 6; display: flex; flex-direction: column;
			border-radius: 9px; overflow: hidden; box-shadow: 0 3px 12px rgba(20,40,80,0.18); border: 1px solid var(--bulma-border, #dbe0e8); background: var(--bulma-scheme-main, #fff); }
		.apwe-zoom-btn { display: flex; align-items: center; justify-content: center; width: 2.1rem; height: 2.1rem; border: none; background: var(--bulma-scheme-main, #fff);
			color: #4a5568; cursor: pointer; font-size: 0.85rem; transition: background 0.12s, color 0.12s; }
		.apwe-zoom-btn + .apwe-zoom-btn { border-top: 1px solid var(--bulma-border, #eef0f4); }
		.apwe-zoom-btn:hover { background: var(--bulma-link, #156dd1); color: #fff; }
		.apwe-legend { position: absolute; left: 0.85rem; bottom: 0.85rem; z-index: 6; background: var(--bulma-scheme-main, #fff);
			border: 1px solid var(--bulma-border, #dbe0e8); border-radius: 9px; box-shadow: 0 3px 12px rgba(20,40,80,0.14); overflow: hidden; }
		.apwe-legend-toggle { display: none; align-items: center; justify-content: center; width: 2.1rem; height: 2.1rem; border: none; background: var(--bulma-scheme-main, #fff); color: #4a5568; cursor: pointer; }
		.apwe-legend-body { padding: 0.6rem 0.75rem; font-size: 0.74rem; color: #4a5568; }
		.apwe-legend.is-collapsed .apwe-legend-body { display: none; }
		.apwe-legend.is-collapsed .apwe-legend-toggle { display: flex; }
		.apwe-legend-title { font-size: 0.62rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--bulma-text-weak, #9aa3b2); margin-bottom: 0.4rem; }
		.apwe-legend-row { display: flex; align-items: center; gap: 0.45rem; line-height: 1.7; white-space: nowrap; }
		.apwe-swatch { display: inline-block; width: 0.85rem; height: 0.85rem; border-radius: 3px; border: 1.5px solid #999; flex-shrink: 0; }
		.apwe-line { display: inline-block; width: 0.9rem; height: 0; border-top: 2px solid #8a93a3; flex-shrink: 0; }
		.apwe-line.dashed { border-top-style: dashed; }
		.apwe-legend-sep { height: 1px; background: var(--bulma-border, #eef0f4); margin: 0.4rem 0; }

		/* --- Side detail / edit panel. --- */
		.apwe-detail { position: absolute; top: 0.85rem; right: 0.85rem; width: 23.5rem; max-width: calc(100% - 1.7rem); z-index: 7; pointer-events: none; }
		.apwe-detail-card { pointer-events: auto; background: var(--bulma-scheme-main, #fff); border: 1px solid var(--bulma-border, #dbe0e8); border-radius: 12px;
			box-shadow: 0 12px 34px rgba(20,40,80,0.20); overflow: hidden; max-height: calc(100vh - 8rem); overflow-y: auto;
			animation: apwe-panel-in 0.16s ease-out; }
		@keyframes apwe-panel-in { from { opacity: 0; transform: translateY(-6px); } }
		.apwe-detail-head { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; padding: 0.7rem 0.85rem;
			background: linear-gradient(180deg, var(--bulma-scheme-main-bis, #f6f8fc), var(--bulma-scheme-main, #fff));
			border-bottom: 1px solid var(--bulma-border, #e6e9ef); border-left: 3px solid var(--bulma-link, #156dd1); }
		.apwe-detail-route { font-weight: 700; font-size: 0.92rem; color: #22334c; display: flex; align-items: center; gap: 0.3rem; }
		.apwe-detail-route .icon { color: var(--bulma-link, #156dd1); }
		.apwe-detail-route .apwe-arrow { color: var(--bulma-text-weak, #8a93a3); margin: 0 0.35rem; }
		.apwe-detail-body { padding: 0.85rem; font-size: 0.9rem; line-height: 1.55; color: #2c3e50; }
		.apwe-state-meta { display: flex; align-items: center; gap: 0.4rem; flex-wrap: wrap; margin-bottom: 0.7rem; }
		.apwe-meta-chip { display: inline-flex; align-items: center; gap: 0.3rem; font-size: 0.72rem; font-weight: 600; color: #5a6b82; background: var(--bulma-scheme-main-bis, #f1f4f9); border-radius: 999px; padding: 0.12rem 0.55rem; }
		.apwe-meta-chip .icon { font-size: 0.68rem; }
		.apwe-state-hash { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.7rem; color: var(--bulma-text-weak, #8a93a3); margin-left: auto; word-break: break-all; }
		.apwe-sublabel { font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; color: var(--bulma-text-weak, #7a8598); margin: 0.2rem 0 0.35rem; }
		.apwe-detail-hint { margin-top: 0.85rem; padding-top: 0.6rem; border-top: 1px solid var(--bulma-border, #eef0f4); font-size: 0.76rem; color: var(--bulma-text-weak, #7a8598); }
		.apwe-detail-hint .icon { font-size: 0.7rem; color: var(--bulma-link, #156dd1); }
		.apwe-apply-hint { margin: 0.4rem 0 0; font-size: 0.72rem; color: var(--bulma-text-weak, #8a93a3); line-height: 1.4; }
		.apwe-apply-hint .icon { font-size: 0.66rem; color: var(--bulma-link, #156dd1); vertical-align: baseline; margin-right: 0.1rem; }
		.apwe-form label { display: block; font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; color: var(--bulma-text-weak, #7a8598); margin: 0.7rem 0 0.2rem; }
		.apwe-form label:first-child { margin-top: 0; }
		.apwe-form .input, .apwe-form select { width: 100%; }
		.apwe-checks { display: grid; grid-template-columns: 1fr 1fr; gap: 0.3rem 0.75rem; margin-top: 0.5rem;
			padding: 0.5rem 0.6rem; background: var(--bulma-scheme-main-bis, #f6f8fc); border-radius: 8px; }
		.apwe-checks label { display: flex; align-items: center; gap: 0.4rem; font-weight: 500; text-transform: none; letter-spacing: 0; color: #2c3e50; font-size: 0.82rem; margin: 0; }
		.apwe-form-actions { display: flex; gap: 0.5rem; margin-top: 0.9rem; }
		.apwe-section-div { height: 1px; background: var(--bulma-border, #eef0f4); margin: 1rem -0.85rem 0; }
		.apwe-trow { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; padding: 0.45rem 0.6rem; border: 1px solid var(--bulma-border, #e6e9ef); border-radius: 8px; margin-bottom: 0.4rem; cursor: pointer; transition: border-color 0.12s, background 0.12s, box-shadow 0.12s; }
		.apwe-trow:hover { background: var(--bulma-scheme-main-bis, #f2f5f9); border-color: var(--bulma-link, #cfe0f6); box-shadow: 0 1px 4px rgba(21,109,209,0.08); }
		.apwe-trow-main { min-width: 0; }
		.apwe-trow-action { font-weight: 600; color: #22334c; }
		.apwe-trow-sub { font-size: 0.76rem; color: var(--bulma-text-weak, #7a7a7a); }
		.apwe-actor-row { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; padding: 0.35rem 0.55rem; background: var(--bulma-scheme-main-bis, #f4f6fa); border: 1px solid var(--bulma-border, #eef0f4); border-radius: 8px; margin-bottom: 0.35rem; }
		.apwe-actor-label { font-size: 0.82rem; color: #2c3e50; min-width: 0; }
		.apwe-level { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; font-size: 0.82rem; padding: 0.45rem 0.7rem; margin-bottom: 0.6rem; border-radius: 8px; background: var(--bulma-scheme-main-bis, #f4f6fa); border: 1px solid var(--bulma-border, #e6e9ef); }
		.apwe-level-link { margin-right: 0.5rem; white-space: nowrap; }
		.apwe-level-revert { margin-left: auto; }
		.apwe-actor-buttons { display: inline-flex; align-items: center; gap: 0.4rem; flex-shrink: 0; }
		.apwe-actor-tag { border: none; background: transparent; color: var(--bulma-text-weak, #7a8598); cursor: pointer; padding: 0.1rem 0.2rem; font-size: 0.75rem; }
		.apwe-actor-tag:hover { color: var(--bulma-link, #156dd1); }
		.apwe-other { margin-top: 0.85rem; padding-top: 0.6rem; border-top: 1px solid var(--bulma-border, #eef0f4); font-size: 0.8rem; }
		.apwe-other-note { font-weight: 400; text-transform: none; letter-spacing: 0; }
		.apwe-other-row { padding: 0.15rem 0; line-height: 1.4; }
		.apwe-other-label { font-weight: 600; margin-right: 0.25rem; }
		.apwe-other-label::after { content: ':'; }
		.apwe-finding { padding: 0.45rem 0.55rem; border: 1px solid var(--bulma-border, #e6e9ef); border-radius: 8px; margin-bottom: 0.4rem; font-size: 0.82rem; line-height: 1.4; cursor: pointer; }
		.apwe-finding:hover { background: var(--bulma-scheme-main-bis, #f2f5f9); }
		.apwe-finding-kind { font-weight: 700; font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.03em; margin-right: 0.25rem; }
		.apwe-finding-error .apwe-finding-kind { color: #D84028; }
		.apwe-finding-warning .apwe-finding-kind { color: #b8860b; }
		.apwe-finding-info .apwe-finding-kind { color: var(--bulma-text-weak, #7a8598); }
		.apwe-json-text { width: 100%; height: 60vh; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.78rem; white-space: pre; }
		.apwe-json-error { color: #D84028; font-size: 0.82rem; margin-bottom: 0.5rem; }
		.apwe-preview { max-height: 50vh; overflow-y: auto; font-size: 0.82rem; }
		.apwe-preview-line { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.74rem; padding: 0.15rem 0; word-break: break-word; }
		.apwe-addactor label { display: block; font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; color: #7a8598; margin: 0.6rem 0 0.2rem; }
		.apwe-addactor .input, .apwe-addactor select { width: 100%; }
		.apwe-user-results { max-height: 9rem; overflow-y: auto; margin-top: 0.35rem; border: 1px solid var(--bulma-border, #eef0f4); border-radius: 6px; }
		.apw-actors { font-weight: 700; color: #22334c; }
		.apw-action { font-style: italic; font-weight: 600; }
		.apw-target { font-weight: 700; color: var(--bulma-link, #156dd1); }
		.apw-branch { color: #7a7a7a; font-style: italic; }
	`,

	Templates:
	[
		{
			Hash: 'ApprovalWorkflow-Editor-Wrap',
			Template: /*html*/`
				<div class="apwe-wrap">
					<div class="apwe-header">
						<div>
							<div class="apwe-crumbs"><a href="#/ApprovalWorkflows"><span class="icon is-small"><i class="fas fa-chevron-left"></i></span> Approval Workflows</a></div>
							<div class="apwe-title"><span class="icon"><i class="fas fa-diagram-project"></i></span> {~D:AppData.ApprovalWorkflowEditor.EditorTitle~} <span class="apw-help" role="button" tabindex="0" title="How to read this diagram" onclick="ApprovalWorkflowHelp.show('editor.graph', this)"><i class="fas fa-circle-info"></i></span></div>
						</div>
						<div class="apwe-actions" id="ApprovalWorkflow-Actions"></div>
					</div>

					{~TS:ApprovalWorkflow-Editor-Error:AppData.ApprovalWorkflowEditor.EditorErrorSlot~}
					<div id="ApprovalWorkflow-LevelBanner"></div>

					<div class="apwe-body">
					<div class="apwe-rail" id="ApprovalWorkflow-RailWrap">
						<div id="ApprovalWorkflow-Rail"></div>
					</div>

					<div class="apwe-flow-host">
						<div id="ApprovalWorkflow-Flow-Container"></div>

						<div id="ApprovalWorkflow-Legend" class="apwe-legend">
							<button class="apwe-legend-toggle" title="Legend" onclick="_Pict.views.ApprovalWorkflowEditor.toggleLegend()"><span class="icon is-small"><i class="fas fa-list"></i></span></button>
							<div class="apwe-legend-body">
								<div class="apwe-legend-title">Legend</div>
								<div class="apwe-legend-row"><span class="apwe-swatch" style="background:#e9f6ea;border-color:#449606"></span> Approved / review</div>
								<div class="apwe-legend-row"><span class="apwe-swatch" style="background:#e7f1fd;border-color:#2196F3"></span> Submitted / pending</div>
								<div class="apwe-legend-row"><span class="apwe-swatch" style="background:#fdece8;border-color:#D84028"></span> Rejected</div>
								<div class="apwe-legend-row"><span class="apwe-swatch" style="background:#f1f2f4;border-color:#6F727A"></span> Draft / other</div>
								<div class="apwe-legend-sep"></div>
								<div class="apwe-legend-row"><span class="apwe-line"></span> Transition</div>
								<div class="apwe-legend-row"><span class="apwe-line dashed"></span> Conditional branch</div>
							</div>
						</div>

						<div class="apwe-zoom">
							<button class="apwe-zoom-btn" title="Zoom in" onclick="_Pict.views.ApprovalWorkflowEditor.graphZoomIn()"><i class="fas fa-plus"></i></button>
							<button class="apwe-zoom-btn" title="Zoom out" onclick="_Pict.views.ApprovalWorkflowEditor.graphZoomOut()"><i class="fas fa-minus"></i></button>
							<button class="apwe-zoom-btn" title="Fit to screen" onclick="_Pict.views.ApprovalWorkflowEditor.graphFit()"><i class="fas fa-expand"></i></button>
						</div>

						<div id="ApprovalWorkflow-Detail" class="apwe-detail"></div>
						{~TS:ApprovalWorkflow-Editor-Loading:AppData.ApprovalWorkflowEditor.EditorLoadingSlot~}
					</div>
					</div>
				</div>`
		},
		{
			Hash: 'ApprovalWorkflow-Rail-Template',
			Template: /*html*/`
				<div class="apwe-rail-head">
					<span class="apwe-rail-title">Steps</span>
					<span class="apwe-rail-count">{~D:Record.CountText~}</span>
					<button class="apwe-rail-toggle" title="Collapse steps" onclick="_Pict.views.ApprovalWorkflowEditor.toggleRail()"><i class="fas fa-angles-left"></i></button>
				</div>
				<div class="apwe-steps">
					{~TS:ApprovalWorkflow-Rail-Step:Record.Steps~}
					{~TS:ApprovalWorkflow-Rail-Empty:Record.EmptySlot~}
				</div>
				{~TS:ApprovalWorkflow-Rail-AddState:Record.AddStateSlot~}`
		},
		{
			Hash: 'ApprovalWorkflow-Rail-Step',
			Template: /*html*/`
				<div class="apwe-step {~D:Record.ActiveClass~}" data-index="{~D:Record.Index~}" title="{~D:Record.StateName~}"
					data-status="{~D:Record.Status~}" onclick="_Pict.views.ApprovalWorkflowEditor.selectStateFromRail(this.dataset.status)">
					<span class="apwe-step-dot" style="background:{~D:Record.Tint~};border-color:{~D:Record.Color~}"></span>
					<div class="apwe-step-main">
						<div class="apwe-step-name">{~D:Record.StateName~}</div>
						<div class="apwe-step-sub">{~D:Record.OutCount~} out{~D:Record.DocumentText~}</div>
					</div>
				</div>`
		},
		{
			Hash: 'ApprovalWorkflow-Rail-AddState',
			Template: /*html*/`<button class="apwe-rail-add" onclick="_Pict.views.ApprovalWorkflowEditor.addStateAndEdit()"><span class="icon is-small"><i class="fas fa-plus"></i></span><span>Add state</span></button>`
		},
		{
			Hash: 'ApprovalWorkflow-Rail-Empty',
			Template: /*html*/`<p class="apwe-rail-empty has-text-grey is-size-7">No states yet.</p>`
		},
		{
			Hash: 'ApprovalWorkflow-Actions-Template',
			Template: /*html*/`
				{~TS:ApprovalWorkflow-Actions-Check:Record.CheckSlot~}
				{~TS:ApprovalWorkflow-Actions-Json:Record.JsonSlot~}
				{~TS:ApprovalWorkflow-Actions-ReadOnly:Record.ReadOnlySlot~}
				{~TS:ApprovalWorkflow-Actions-View:Record.ViewModeSlot~}
				{~TS:ApprovalWorkflow-Actions-Dirty:Record.DirtySlot~}
				{~TS:ApprovalWorkflow-Actions-Delete:Record.DeleteSlot~}
				{~TS:ApprovalWorkflow-Actions-Edit:Record.EditModeSlot~}`
		},
		{
			Hash: 'ApprovalWorkflow-Actions-ReadOnly',
			Template: /*html*/`<span class="apwe-badge" title="Editing requires customer-admin permission."><span class="icon is-small"><i class="fas fa-eye"></i></span> Read only</span>`
		},
		{
			Hash: 'ApprovalWorkflow-Actions-View',
			Template: /*html*/`<span class="apw-help" role="button" tabindex="0" title="About editing" onclick="ApprovalWorkflowHelp.show('editor.edit', this)"><i class="fas fa-circle-info"></i></span><button class="button is-small is-link" onclick="_Pict.views.ApprovalWorkflowEditor.toggleEditMode()"><span class="icon is-small"><i class="fas fa-pen"></i></span><span>Edit</span></button>`
		},
		{
			Hash: 'ApprovalWorkflow-Actions-Dirty',
			Template: /*html*/`<span class="apwe-dirty"><span class="icon is-small"><i class="fas fa-circle"></i></span> Unsaved changes</span>`
		},
		{
			Hash: 'ApprovalWorkflow-Actions-Edit',
			Template: /*html*/`
				<button class="button is-small" onclick="_Pict.views.ApprovalWorkflowEditor.addStateAndEdit()"><span class="icon is-small"><i class="fas fa-plus"></i></span><span>Add state</span></button>
				<button class="button is-small is-light" onclick="_Pict.views.ApprovalWorkflowEditor.discardChanges()">Discard</button>
				<button class="button is-small is-success" onclick="_Pict.views.ApprovalWorkflowEditor.saveWorkflow()"><span class="icon is-small"><i class="fas fa-floppy-disk"></i></span><span>Save</span></button>
				<button class="button is-small is-link is-light" onclick="_Pict.views.ApprovalWorkflowEditor.toggleEditMode()">Done</button>`
		},
		{
			Hash: 'ApprovalWorkflow-Detail-Template',
			Template: /*html*/`
				<div class="apwe-detail-card">
					<div class="apwe-detail-head">
						<span class="apwe-detail-route">{~D:Record.SourceStateName~}<span class="apwe-arrow">→</span>{~D:Record.ToStateName~}</span>
						<button class="delete" aria-label="close" onclick="_Pict.views.ApprovalWorkflowEditor.closeDetail()"></button>
					</div>
					<div class="apwe-detail-body">
						{~TS:ApprovalWorkflow-Detail-Branch:Record.BranchSlot~}
						{~D:Record.SentenceText~}
						{~TS:ApprovalWorkflow-OtherSettings:Record.OtherSlot~}
					</div>
				</div>`
		},
		{
			Hash: 'ApprovalWorkflow-OtherSettings',
			Template: /*html*/`
				<div class="apwe-other">
					<div class="apwe-sublabel">Other settings <span class="apwe-other-note">(kept when you save)</span></div>
					{~TS:ApprovalWorkflow-OtherSetting:Record.Rows~}
				</div>`
		},
		{
			Hash: 'ApprovalWorkflow-OtherSetting',
			Template: /*html*/`<div class="apwe-other-row"><span class="apwe-other-label">{~D:Record.Label~}</span> <span>{~D:Record.Detail~}</span></div>`
		},
		{
			Hash: 'ApprovalWorkflow-Findings-Template',
			Template: /*html*/`
				<div class="apwe-detail-card">
					<div class="apwe-detail-head">
						<span class="apwe-detail-route"><span class="icon is-small"><i class="fas fa-triangle-exclamation"></i></span> Things to check</span>
						<button class="delete" aria-label="close" onclick="_Pict.views.ApprovalWorkflowEditor.closeDetail()"></button>
					</div>
					<div class="apwe-detail-body">
						{~TS:ApprovalWorkflow-Finding:Record.Findings~}
						{~TS:ApprovalWorkflow-Findings-Empty:Record.EmptySlot~}
					</div>
				</div>`
		},
		{
			Hash: 'ApprovalWorkflow-Finding',
			Template: /*html*/`<div class="apwe-finding apwe-finding-{~D:Record.Severity~}" onclick="_Pict.views.ApprovalWorkflowEditor.openFinding({~D:Record.Index~})"><span class="apwe-finding-kind">{~D:Record.Kind~}</span> {~D:Record.Message~}</div>`
		},
		{
			Hash: 'ApprovalWorkflow-Findings-Empty',
			Template: /*html*/`<p class="has-text-grey is-size-7">Nothing to check.</p>`
		},
		{
			Hash: 'ApprovalWorkflow-Actions-Delete',
			Template: /*html*/`<button class="button is-small is-danger is-light" onclick="_Pict.views.ApprovalWorkflowEditor.deleteWorkflow()">Delete workflow</button>`
		},
		{
			Hash: 'ApprovalWorkflow-LevelBanner-Template',
			Template: /*html*/`{~TS:ApprovalWorkflow-LevelBanner:Record.Slot~}`
		},
		{
			Hash: 'ApprovalWorkflow-LevelBanner',
			Template: /*html*/`
				<div class="apwe-level">
					<span class="icon is-small"><i class="fas fa-layer-group"></i></span>
					<span>{~D:Record.Message~} {~TS:ApprovalWorkflow-LevelLink:Record.Links~}</span>
					{~TS:ApprovalWorkflow-LevelRevert:Record.RevertSlot~}
				</div>`
		},
		{
			Hash: 'ApprovalWorkflow-LevelLink',
			Template: /*html*/`<a class="apwe-level-link" href="{~D:Record.Href~}">{~D:Record.Label~}</a>`
		},
		{
			Hash: 'ApprovalWorkflow-LevelRevert',
			Template: /*html*/`<button class="button is-small is-light apwe-level-revert" onclick="_Pict.views.ApprovalWorkflowEditor.revertToInherited()">{~D:Record.Label~}</button>`
		},
		{
			Hash: 'ApprovalWorkflow-Actions-Check',
			Template: /*html*/`<button class="button is-small is-warning is-light" onclick="_Pict.views.ApprovalWorkflowEditor.showFindings()"><span class="icon is-small"><i class="fas fa-triangle-exclamation"></i></span><span>{~D:Record.Count~} thing{~D:Record.Plural~} to check</span></button>`
		},
		{
			Hash: 'ApprovalWorkflow-Actions-Json',
			Template: /*html*/`<button class="button is-small is-light" title="See this workflow's full configuration" onclick="_Pict.views.ApprovalWorkflowEditor.openJsonEditor()"><span class="icon is-small"><i class="fas fa-code"></i></span><span>JSON</span></button>`
		},
		{
			Hash: 'ApprovalWorkflow-Detail-Branch',
			Template: /*html*/`<div class="mb-2"><span class="tag is-warning is-light">Conditional</span></div>`
		},
		{
			Hash: 'ApprovalWorkflow-BranchReadonly-Template',
			Template: /*html*/`
				<div class="apwe-detail-card">
					<div class="apwe-detail-head">
						<span class="apwe-detail-route">{~D:Record.SourceStateName~}<span class="apwe-arrow">→</span>{~D:Record.ToStateName~}</span>
						<button class="delete" aria-label="close" onclick="_Pict.views.ApprovalWorkflowEditor.closeDetail()"></button>
					</div>
					<div class="apwe-detail-body">
						<div class="mb-2"><span class="tag is-warning is-light">Conditional branch — read only</span></div>
						{~D:Record.SentenceText~}
						<p class="has-text-grey is-size-7 mt-2">Branch conditions aren't editable here yet.</p>
					</div>
				</div>`
		},
		{
			Hash: 'ApprovalWorkflow-StateDetail-Template',
			Template: /*html*/`
				<div class="apwe-detail-card">
					<div class="apwe-detail-head">
						<span class="apwe-detail-route"><span class="icon is-small"><i class="fas fa-circle-nodes"></i></span> {~D:Record.StateName~}</span>
						<button class="delete" aria-label="close" onclick="_Pict.views.ApprovalWorkflowEditor.closeDetail()"></button>
					</div>
					<div class="apwe-detail-body">
						<div class="apwe-state-meta">
							<span class="apwe-meta-chip"><span class="icon is-small"><i class="fas fa-arrow-right-to-bracket"></i></span> {~D:Record.IncomingText~}</span>
							<span class="apwe-meta-chip"><span class="icon is-small"><i class="fas fa-arrow-right-from-bracket"></i></span> {~D:Record.OutgoingText~}</span>
							<span class="apwe-state-hash" title="State identity">{~D:Record.Status~}</span>
						</div>
						<div class="apwe-sublabel">Transitions out of this state</div>
						{~TS:ApprovalWorkflow-StateDetail-Transition:Record.Transitions~}
						{~TS:ApprovalWorkflow-StateDetail-Empty:Record.EmptySlot~}
						{~TS:ApprovalWorkflow-StateDetail-Hint:Record.EditHintSlot~}
					</div>
				</div>`
		},
		{
			Hash: 'ApprovalWorkflow-StateDetail-Transition',
			Template: /*html*/`
				<div class="apwe-trow" data-status="{~D:Record.Status~}" data-index="{~D:Record.TransitionIndex~}" onclick="_Pict.views.ApprovalWorkflowEditor.viewTransitionDetail(this.dataset.status, this.dataset.index)">
					<div class="apwe-trow-main"><div class="apwe-trow-action">{~D:Record.Action~}</div><div class="apwe-trow-sub">→ {~D:Record.ToStateName~}</div></div>
					<span class="icon has-text-grey-light"><i class="fas fa-chevron-right"></i></span>
				</div>`
		},
		{
			Hash: 'ApprovalWorkflow-StateDetail-Empty',
			Template: /*html*/`<p class="has-text-grey is-size-7">This is a terminal state — no transitions leave it.</p>`
		},
		{
			Hash: 'ApprovalWorkflow-StateDetail-Hint',
			Template: /*html*/`<p class="apwe-detail-hint"><span class="icon is-small"><i class="fas fa-pen"></i></span> Click <strong>Edit</strong> (top right) to modify this state.</p>`
		},
		{
			Hash: 'ApprovalWorkflow-TransitionEditor-Template',
			Template: /*html*/`
				<div class="apwe-detail-card">
					<div class="apwe-detail-head">
						<span class="apwe-detail-route"><span class="icon is-small"><i class="fas fa-arrow-right-arrow-left"></i></span> Transition from {~D:Record.SourceStateName~}</span>
						<button class="delete" aria-label="close" onclick="_Pict.views.ApprovalWorkflowEditor.closeDetail()"></button>
					</div>
					<div class="apwe-detail-body apwe-form">
						<label>Action <span class="apw-help" role="button" tabindex="0" title="What's this?" onclick="ApprovalWorkflowHelp.show('transition.action', this)"><i class="fas fa-circle-info"></i></span></label>
						<input type="text" id="apw-edit-action" class="input is-small" value="{~D:Record.Action~}" />
						<label>Goes to state <span class="apw-help" role="button" tabindex="0" title="What's this?" onclick="ApprovalWorkflowHelp.show('transition.target', this)"><i class="fas fa-circle-info"></i></span></label>
						<div class="select is-small is-fullwidth"><select id="apw-edit-target">{~TS:ApprovalWorkflow-Option:Record.Options~}</select></div>
						<label>Options <span class="apw-help" role="button" tabindex="0" title="What's this?" onclick="ApprovalWorkflowHelp.show('transition.flags', this)"><i class="fas fa-circle-info"></i></span></label>
						<div class="apwe-checks">
							<label><input type="checkbox" id="apw-edit-lock" {~D:Record.LockChecked~}> Locks entity</label>
							<label><input type="checkbox" id="apw-edit-unlock" {~D:Record.UnlockChecked~}> Unlocks entity</label>
							<label><input type="checkbox" id="apw-edit-approval" {~D:Record.ApprovalChecked~}> Is an approval</label>
							<label><input type="checkbox" id="apw-edit-allowlocked" {~D:Record.AllowLockedChecked~}> Allowed on locked</label>
						</div>
						<div class="apwe-form-actions">
							<button class="button is-small is-link" title="Put these edits on the diagram. Nothing is stored until you press Save (top right)." onclick="_Pict.views.ApprovalWorkflowEditor.applyTransitionForm()"><span class="icon is-small"><i class="fas fa-arrow-up-right-from-square"></i></span><span>Apply to diagram</span></button>
							<button class="button is-small is-danger is-light" onclick="_Pict.views.ApprovalWorkflowEditor.deleteTransitionFromForm()">Delete</button>
						</div>
						<p class="apwe-apply-hint"><span class="icon is-small"><i class="fas fa-circle-info"></i></span> Updates the diagram now; press <strong>Save</strong> (top right) to store it.</p>

						<div class="apwe-section-div"></div>
						<label>Who can perform this action <span class="apw-help" role="button" tabindex="0" title="What's this?" onclick="ApprovalWorkflowHelp.show('transition.actors', this)"><i class="fas fa-circle-info"></i></span></label>
						{~TS:ApprovalWorkflow-ActorRow:Record.Actors~}
						{~TS:ApprovalWorkflow-ActorEmpty:Record.ActorEmptySlot~}
						<div class="apwe-form-actions">
							<button class="button is-small is-light" onclick="_Pict.views.ApprovalWorkflowEditor.openAddActorModal()"><span class="icon is-small"><i class="fas fa-user-plus"></i></span><span>Add actor</span></button>
						</div>
						{~TS:ApprovalWorkflow-OtherSettings:Record.OtherSlot~}
					</div>
				</div>`
		},
		{
			Hash: 'ApprovalWorkflow-ActorRow',
			Template: /*html*/`
				<div class="apwe-actor-row">
					<span class="apwe-actor-label">{~D:Record.Label~}</span>
					<span class="apwe-actor-buttons">
						<button class="apwe-actor-tag" title="Set tags, which decide who gets notified" aria-label="set tags" onclick="_Pict.views.ApprovalWorkflowEditor.openActorTagsModal({~D:Record.ActorIndex~},{~D:Record.SettingIndex~})"><i class="fas fa-tag"></i></button>
						<button class="delete is-small" aria-label="remove" onclick="_Pict.views.ApprovalWorkflowEditor.removeActor({~D:Record.ActorIndex~},{~D:Record.SettingIndex~})"></button>
					</span>
				</div>`
		},
		{
			Hash: 'ApprovalWorkflow-ActorEmpty',
			Template: /*html*/`<p class="has-text-grey is-size-7">No one is assigned — this action can't be performed until an actor is added.</p>`
		},
		{
			Hash: 'ApprovalWorkflow-Option',
			Template: /*html*/`<option value="{~D:Record.Status~}" {~D:Record.SelectedAttr~}>{~D:Record.StateName~}</option>`
		},
		{
			Hash: 'ApprovalWorkflow-StateEditor-Template',
			Template: /*html*/`
				<div class="apwe-detail-card">
					<div class="apwe-detail-head">
						<span class="apwe-detail-route"><span class="icon is-small"><i class="fas fa-circle-nodes"></i></span> State</span>
						<button class="delete" aria-label="close" onclick="_Pict.views.ApprovalWorkflowEditor.closeDetail()"></button>
					</div>
					<div class="apwe-detail-body apwe-form">
						<label>Display name <span class="apw-help" role="button" tabindex="0" title="What's this?" onclick="ApprovalWorkflowHelp.show('state.name', this)"><i class="fas fa-circle-info"></i></span></label>
						<input type="text" id="apw-state-name" class="input is-small" value="{~D:Record.StateName~}" />
						<label>State hash (identity) <span class="apw-help" role="button" tabindex="0" title="What's this?" onclick="ApprovalWorkflowHelp.show('state.status', this)"><i class="fas fa-circle-info"></i></span></label>
						<input type="text" id="apw-state-status" class="input is-small" value="{~D:Record.Status~}" />
						<div class="apwe-form-actions">
							<button class="button is-small is-link" title="Put these edits on the diagram. Nothing is stored until you press Save (top right)." onclick="_Pict.views.ApprovalWorkflowEditor.applyStateForm()"><span class="icon is-small"><i class="fas fa-arrow-up-right-from-square"></i></span><span>Apply to diagram</span></button>
							<button class="button is-small is-danger is-light" onclick="_Pict.views.ApprovalWorkflowEditor.deleteStateFromForm()">Delete state</button>
						</div>
						<p class="apwe-apply-hint"><span class="icon is-small"><i class="fas fa-circle-info"></i></span> Updates the diagram now; press <strong>Save</strong> (top right) to store it.</p>
						<label class="mt-3">Transitions out of this state <span class="apw-help" role="button" tabindex="0" title="What's this?" onclick="ApprovalWorkflowHelp.show('state.transitions', this)"><i class="fas fa-circle-info"></i></span></label>
						{~TS:ApprovalWorkflow-StateEditor-Transition:Record.Transitions~}
						{~TS:ApprovalWorkflow-StateEditor-Empty:Record.EmptySlot~}
						<div class="apwe-form-actions">
							<button class="button is-small is-light" onclick="_Pict.views.ApprovalWorkflowEditor.addTransitionFromState()"><span class="icon is-small"><i class="fas fa-plus"></i></span><span>Add transition</span></button>
						</div>
					</div>
				</div>`
		},
		{
			Hash: 'ApprovalWorkflow-StateEditor-Transition',
			Template: /*html*/`
				<div class="apwe-trow" data-status="{~D:Record.Status~}" data-index="{~D:Record.TransitionIndex~}" onclick="_Pict.views.ApprovalWorkflowEditor.openTransitionEditor(this.dataset.status, this.dataset.index)">
					<div class="apwe-trow-main"><div class="apwe-trow-action">{~D:Record.Action~}</div><div class="apwe-trow-sub">→ {~D:Record.ToStatus~}</div></div>
					<span class="icon has-text-grey-light"><i class="fas fa-chevron-right"></i></span>
				</div>`
		},
		{
			Hash: 'ApprovalWorkflow-StateEditor-Empty',
			Template: /*html*/`<p class="has-text-grey is-size-7">No transitions leave this state.</p>`
		},
		{
			Hash: 'ApprovalWorkflow-Editor-Loading',
			Template: /*html*/`<div class="apwe-overlay has-text-grey"><span class="apwe-spinner"></span><p class="mt-3">Loading workflow…</p></div>`
		},
		{
			Hash: 'ApprovalWorkflow-Editor-Error',
			Template: /*html*/`<div class="notification is-danger is-soft py-2 px-3 mb-3"><span class="icon"><i class="fas fa-triangle-exclamation"></i></span> <span>{~D:Record.Message~}</span></div>`
		}
	],

	Renderables:
	[
		{ RenderableHash: 'ApprovalWorkflowEditorWrap', TemplateHash: 'ApprovalWorkflow-Editor-Wrap', ContentDestinationAddress: '#PlatformConfiguration-Application-Container' }
	]
};
